import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PageContent } from 'src/pages/page-content.js';
import { t } from 'src/i18n';
import 'src/utilities/display-alert';
import '../../content/d-entity-header.js';
import '../../library/components/d-help-section.js';
import '../../library/elements/d-action.js';
import type { PageViewModel } from '@trinnvis/dabih-api-client';
import { type ItemGroup } from 'src/pages/organization-page/organization-page-view-model';
import { features } from '../../store/selectors/features/features';
import type { IssuesPageViewModel } from './issues-page-view-model';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { PageLink } from 'src/pages/overview-page/overview-page-view-model';
import '../d-no-items-prompt';
import '../../library/elements/d-more-menu';
import { DIssueEscalationDialog } from '../../content/issues/d-issue-escalation-dialog';
import type { IssueEscalationDialogResult } from '../../content/issues/issue-escalation';

@customElement('d-issues-page-content')
export class DIssuesPageContent extends PageContent<IssuesPageViewModel> {
  static readonly styles = [...super.styles];
  @property({ type: Object })
  currentPageItem: any = {};
  @property({ type: Object })
  route = {};
  @property({ type: Object })
  queryParams = {};
  @property({ type: Array })
  pages: PageViewModel[] = [];
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  editMode = false;
  @property({ type: Object })
  token = {};
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: String })
  key = '';
  @property({ type: Number })
  organizationId = 0;

  onCreateIssue() {
    this.dispatchEvent(new CustomEvent('display-new-issue', { composed: true, bubbles: true, detail: {} }));
  }

  private async onMoreMenuAction(action: string) {
    if (action === 'escalation') {
      await this.openEscalationDialog();
    }
  }

  private async openEscalationDialog() {
    const organizationId = this.pageView.organizationId;
    const result: IssueEscalationDialogResult = await DIssueEscalationDialog.open({
      organizationId,
      settings: this.pageView.escalationSettings,
      personOptions: this.pageView.notifyPersonOptions,
      candidates: this.pageView.escalationCandidates,
    });
    if (result.action === 'save') {
      this.dispatchEvent(
        new CustomEvent('update-issue-escalation', {
          bubbles: true,
          composed: true,
          detail: { settings: result.settings, extensions: result.extensions },
        }),
      );
    }
  }

  private pageLinkStyle(type: string) {
    return 'background-image: url(/images/' + type + '-color.svg)';
  }
  renderPageLinks(list: PageLink[]) {
    if (list.length) {
      return html` <div class="page-links">
        ${list.map((pageLink: PageLink) => {
          return html`<a class="page-link" style="${this.pageLinkStyle(pageLink.type)}" href="${pageLink.href}"
            >${pageLink.name}</a
          >`;
        })}
      </div>`;
    }
    return nothing;
  }

  renderContent() {
    if (this.pageView.promoData) {
      return html` <d-promo-section
        theme-page
        .employeesCount=${this.pageView.employeesCount}
        .specialTerms=${this.pageView.specialTerms}
        .promoData=${this.pageView.promoData}
        .features=${features}
        .canUpgrade=${this.writeAccess}
      ></d-promo-section>`;
    }
    if (this.pageView.issues.length) {
      return html`
        ${this.renderPageLinks(this.pageView.pageLinks)}
        <d-list-section
          theme-page
          no-header
          .items=${this.asListItem(this.pageView.issues)}
          icon="issues"
          organization-id="${this.pageView.organizationId}"
          .writeAccess=${this.writeAccess}
          .contentStickyTop=${this.contentStickyTop}
        >
        </d-list-section>
      `;
    }
    return html`
      ${this.renderPageLinks(this.pageView.pageLinks)}
      <d-no-items-prompt
        entity-type="issues"
        heading="${t('issues.noIssuesHeading')}"
        prompt="${t('issues.noIssuesPrompt')}"
        .writeAccess=${this.pageView.writeAccess}
        @click=${() => this.onCreateIssue()}
      ></d-no-items-prompt>
    `;
  }

  protected renderCustomActions(): TemplateResult | typeof nothing {
    return html`${this.pageView.writeAccess
      ? html`<d-action class="action" @click=${() => this.onCreateIssue()}>${t('issues.newIssue')}</d-action>`
      : nothing}
    ${!this.singleUserVersion
      ? html`<d-action class="action" @click=${() => this.onShareContent()}>${t('content.share')}</d-action>`
      : nothing}
    ${this.pageView.writeAccess
      ? html`<d-more-menu
          .actions=${[{ name: t('issues.escalation.menuAction'), action: 'escalation', href: '' }]}
          @action=${(e: CustomEvent) => this.onMoreMenuAction(e.detail)}
        ></d-more-menu>`
      : nothing}`;
  }

  private asListItem(list: ItemGroup[]): ListSectionItemInput[] {
    return list.map((v) => ({
      label: v.label,
      accessible: false,
      sublistToggle: v.filtered,
      sublistHidden: v.filtered,
      items: v.items.map((item) => ({
        label: item.dateDisplay + ' ' + item.label,
        href: item.href,
        mainIcon: item.locked ? 'lock' : undefined,
        accessible: item.accessible,
        hasDraft: item.hasDraft,
      })),
    }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issues-page-content': DIssuesPageContent;
  }
}
